/**
 * CoachPlaybook - ScreenRecorder Component
 * Description: Crop-select screen recorder for GIF and MP4/WebM export.
 * User drags a selection rectangle, hits record, play animates, frames captured from live DOM.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import GIF from 'gif.js';

const C = {
  overlay: 'rgba(0,0,0,0.5)',
  border: '#22d3ee',
  handle: '#22d3ee',
  bg: '#0f172a',
  text: '#f1f5f9',
  textDim: '#94a3b8',
  accent: '#22d3ee',
  danger: '#ef4444',
  success: '#22c55e',
};

const HANDLE_SIZE = 10;

const ScreenRecorder = ({ onClose, onStartPlayback, onStopPlayback, surfaceRef, isPlayingBack }) => {
  const [phase, setPhase] = useState('select'); // select | recording | processing
  const [crop, setCrop] = useState(null); // { x, y, w, h } in screen coords
  const [dragging, setDragging] = useState(null); // 'move' | 'nw' | 'ne' | 'sw' | 'se' | null
  const [dragStart, setDragStart] = useState(null);
  const [format, setFormat] = useState('gif'); // gif | webm
  const [fps, setFps] = useState(30);
  const [speed, setSpeed] = useState(1); // 0.5x, 1x, 2x playback speed
  const [progress, setProgress] = useState(0);
  const framesRef = useRef([]);
  const recordingRef = useRef(false);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const animFrameRef = useRef(null);

  // Auto-stop recording when playback ends (transition from playing → stopped)
  const autoStopRef = useRef(false);
  const wasPlayingRef = useRef(false);
  useEffect(() => {
    if (phase === 'recording') {
      if (isPlayingBack) {
        wasPlayingRef.current = true;
      } else if (wasPlayingRef.current) {
        // Was playing, now stopped — auto-stop
        wasPlayingRef.current = false;
        autoStopRef.current = true;
      }
    } else {
      wasPlayingRef.current = false;
    }
  }, [isPlayingBack, phase]);

  // Auto-snap crop to the SVG field corners
  useEffect(() => {
    const svg = surfaceRef?.current?.querySelector('svg');
    if (svg) {
      const rect = svg.getBoundingClientRect();
      setCrop({
        x: Math.round(rect.left),
        y: Math.round(rect.top),
        w: Math.round(rect.width),
        h: Math.round(rect.height),
      });
    } else if (surfaceRef?.current) {
      const rect = surfaceRef.current.getBoundingClientRect();
      setCrop({
        x: Math.round(rect.left),
        y: Math.round(rect.top),
        w: Math.round(rect.width),
        h: Math.round(rect.height),
      });
    } else {
      setCrop({ x: 100, y: 100, w: 600, h: 400 });
    }
  }, [surfaceRef]);

  // Mouse handlers for crop selection
  const handleMouseDown = useCallback((e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(type);
    setDragStart({ mx: e.clientX, my: e.clientY, ...crop });
  }, [crop]);

  const handleMouseMove = useCallback((e) => {
    if (!dragging || !dragStart) return;
    const dx = e.clientX - dragStart.mx;
    const dy = e.clientY - dragStart.my;

    if (dragging === 'move') {
      setCrop({ ...crop, x: dragStart.x + dx, y: dragStart.y + dy });
    } else if (dragging === 'se') {
      setCrop({ ...crop, w: Math.max(100, dragStart.w + dx), h: Math.max(80, dragStart.h + dy) });
    } else if (dragging === 'sw') {
      const newW = Math.max(100, dragStart.w - dx);
      setCrop({ ...crop, x: dragStart.x + dragStart.w - newW, w: newW, h: Math.max(80, dragStart.h + dy) });
    } else if (dragging === 'ne') {
      setCrop({ ...crop, w: Math.max(100, dragStart.w + dx), y: dragStart.y + dy, h: Math.max(80, dragStart.h - dy) });
    } else if (dragging === 'nw') {
      const newW = Math.max(100, dragStart.w - dx);
      const newH = Math.max(80, dragStart.h - dy);
      setCrop({ ...crop, x: dragStart.x + dragStart.w - newW, y: dragStart.y + dragStart.h - newH, w: newW, h: newH });
    }
  }, [dragging, dragStart, crop]);

  const handleMouseUp = useCallback(() => {
    setDragging(null);
    setDragStart(null);
  }, []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  // Capture a single frame from the screen region
  const captureFrame = useCallback(async () => {
    if (!crop) return null;
    try {
      // Use html2canvas-style approach: draw the DOM region to canvas
      const { x, y, w, h } = crop;
      const scale = 2; // retina quality
      const canvas = document.createElement('canvas');
      canvas.width = w * scale;
      canvas.height = h * scale;
      const ctx = canvas.getContext('2d');

      // Find all elements within the crop region and draw them
      // For SVG-based content, we capture the SVG element directly
      const svg = surfaceRef?.current?.querySelector('svg');
      if (svg) {
        const svgRect = svg.getBoundingClientRect();
        // Calculate what portion of the SVG our crop covers
        const offsetX = x - svgRect.left;
        const offsetY = y - svgRect.top;
        const scaleX = svg.viewBox.baseVal.width / svgRect.width;
        const scaleY = svg.viewBox.baseVal.height / svgRect.height;

        // Clone SVG with inline styles for serialization
        const svgClone = svg.cloneNode(true);
        svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        svgClone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

        // Adjust viewBox to show only the cropped region
        const vbX = svg.viewBox.baseVal.x + offsetX * scaleX;
        const vbY = svg.viewBox.baseVal.y + offsetY * scaleY;
        const vbW = w * scaleX;
        const vbH = h * scaleY;
        svgClone.setAttribute('viewBox', `${vbX} ${vbY} ${vbW} ${vbH}`);
        svgClone.setAttribute('width', w * scale);
        svgClone.setAttribute('height', h * scale);

        // Inline all image hrefs as data URLs
        const images = svgClone.querySelectorAll('image');
        for (const img of images) {
          const href = img.getAttribute('href') || '';
          if (href && !href.startsWith('data:')) {
            try {
              const fullUrl = href.startsWith('/') ? `${window.location.origin}${href}` : href;
              const resp = await fetch(fullUrl);
              const blob = await resp.blob();
              const dataUrl = await new Promise(resolve => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });
              img.setAttribute('href', dataUrl);
            } catch {
              // Keep original href
            }
          }
        }

        const svgData = new XMLSerializer().serializeToString(svgClone);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);
        const svgImg = new Image();
        await new Promise(r => {
          svgImg.onload = r;
          svgImg.onerror = () => { console.warn('[Recorder] SVG frame render failed'); r(); };
          svgImg.src = svgUrl;
        });
        URL.revokeObjectURL(svgUrl);

        if (svgImg.complete && svgImg.naturalWidth > 0) {
          ctx.drawImage(svgImg, 0, 0, w * scale, h * scale);
        }
      }

      return canvas;
    } catch (err) {
      console.error('[Recorder] Frame capture error:', err);
      return null;
    }
  }, [crop, surfaceRef]);

  // Start recording
  const startRecording = useCallback(async () => {
    if (!crop) return;
    setPhase('recording');
    recordingRef.current = true;
    framesRef.current = [];
    chunksRef.current = [];
    setProgress(0);

    // Pre-cache all texture data URLs before recording starts
    const svg = surfaceRef?.current?.querySelector('svg');
    if (svg) {
      const images = svg.querySelectorAll('image');
      const dataUrlCache = {};
      for (const img of images) {
        const href = img.getAttribute('href') || '';
        if (href && !href.startsWith('data:')) {
          try {
            const fullUrl = href.startsWith('/') ? `${window.location.origin}${href}` : href;
            const resp = await fetch(fullUrl);
            const blob = await resp.blob();
            dataUrlCache[href] = await new Promise(resolve => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
          } catch { /* skip */ }
        }
      }
      // Store cache for frame capture
      window._recorderDataUrlCache = dataUrlCache;
    }

    // Start playback
    onStartPlayback?.();

    // Capture frames at the specified FPS
    const interval = 1000 / fps;
    let lastCapture = 0;
    let frameCount = 0;

    const captureLoop = async (timestamp) => {
      if (!recordingRef.current || autoStopRef.current) {
        // Auto-stop triggered — finish up
        if (autoStopRef.current) {
          autoStopRef.current = false;
          recordingRef.current = false;
          onStopPlayback?.();
          // Trigger processing after a tick
          setTimeout(() => processOutput(), 100);
        }
        return;
      }

      if (timestamp - lastCapture >= interval) {
        lastCapture = timestamp;
        const canvas = await captureFrameCached();
        if (canvas) {
          if (format === 'gif') {
            framesRef.current.push(canvas);
          }
          frameCount++;
          setProgress(frameCount);
        }
      }

      animFrameRef.current = requestAnimationFrame(captureLoop);
    };

    animFrameRef.current = requestAnimationFrame(captureLoop);

    // For WebM, use MediaRecorder on a canvas stream
    if (format === 'webm') {
      const { w, h } = crop;
      const recCanvas = document.createElement('canvas');
      recCanvas.width = w * 2;
      recCanvas.height = h * 2;
      canvasRef.current = recCanvas;

      const stream = recCanvas.captureStream(fps);
      const recorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9',
        videoBitsPerSecond: 5000000,
      });
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.start(100); // collect chunks every 100ms
      mediaRecorderRef.current = recorder;

      // Draw frames to the recording canvas
      const drawLoop = async () => {
        if (!recordingRef.current) return;
        const frame = await captureFrameCached();
        if (frame && canvasRef.current) {
          const ctx = canvasRef.current.getContext('2d');
          ctx.drawImage(frame, 0, 0);
        }
        requestAnimationFrame(drawLoop);
      };
      requestAnimationFrame(drawLoop);
    }
  }, [crop, fps, format, surfaceRef, onStartPlayback]);

  // Cached frame capture (uses pre-loaded data URLs)
  const captureFrameCached = useCallback(async () => {
    if (!crop) return null;
    try {
      const { x, y, w, h } = crop;
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = w * scale;
      canvas.height = h * scale;
      const ctx = canvas.getContext('2d');

      const svg = surfaceRef?.current?.querySelector('svg');
      if (svg) {
        const svgRect = svg.getBoundingClientRect();
        const offsetX = x - svgRect.left;
        const offsetY = y - svgRect.top;
        const scX = svg.viewBox.baseVal.width / svgRect.width;
        const scY = svg.viewBox.baseVal.height / svgRect.height;

        const svgClone = svg.cloneNode(true);
        svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        svgClone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

        const vbX = svg.viewBox.baseVal.x + offsetX * scX;
        const vbY = svg.viewBox.baseVal.y + offsetY * scY;
        const vbW = w * scX;
        const vbH = h * scY;
        svgClone.setAttribute('viewBox', `${vbX} ${vbY} ${vbW} ${vbH}`);
        svgClone.setAttribute('width', w * scale);
        svgClone.setAttribute('height', h * scale);

        // Use cached data URLs
        const cache = window._recorderDataUrlCache || {};
        const images = svgClone.querySelectorAll('image');
        for (const img of images) {
          const href = img.getAttribute('href') || '';
          if (href && !href.startsWith('data:') && cache[href]) {
            img.setAttribute('href', cache[href]);
          }
        }

        const svgData = new XMLSerializer().serializeToString(svgClone);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);
        const svgImg = new Image();
        await new Promise(r => {
          svgImg.onload = r;
          svgImg.onerror = r;
          svgImg.src = svgUrl;
        });
        URL.revokeObjectURL(svgUrl);

        if (svgImg.complete && svgImg.naturalWidth > 0) {
          ctx.drawImage(svgImg, 0, 0, w * scale, h * scale);
        }
      }
      return canvas;
    } catch {
      return null;
    }
  }, [crop, surfaceRef]);

  // Process captured frames into GIF or WebM
  const processOutput = useCallback(async () => {
    setPhase('processing');

    if (format === 'gif') {
      const frames = framesRef.current;
      if (frames.length === 0) { setPhase('select'); return; }

      const { w, h } = crop;
      const scale = 2;
      const gif = new GIF({
        workers: 2,
        quality: 8,
        width: w * scale,
        height: h * scale,
        workerScript: '/gif.worker.js',
      });

      const minDelay = 80; // phones ignore delays under ~50ms
      frames.forEach((canvas, i) => {
        const speedScale = 1 / speed;
        let delay;
        if (i === 0) {
          delay = Math.max(minDelay, Math.round(800 * speedScale));
        } else if (i === frames.length - 1) {
          delay = Math.max(minDelay, Math.round(1200 * speedScale));
        } else {
          delay = Math.max(minDelay, Math.round((1000 / fps) * speedScale));
        }
        gif.addFrame(canvas, { delay, copy: true });
      });

      gif.on('progress', (p) => setProgress(Math.round(p * 100)));
      gif.on('finished', (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `play-recording-${Date.now()}.gif`;
        a.click();
        URL.revokeObjectURL(url);
        setPhase('done');
        setProgress(100);
        delete window._recorderDataUrlCache;
      });

      gif.render();
    } else if (format === 'webm') {
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop();
        await new Promise(r => setTimeout(r, 500));
      }

      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `play-recording-${Date.now()}.webm`;
      a.click();
      URL.revokeObjectURL(url);
      setPhase('done');
      setProgress(100);
      delete window._recorderDataUrlCache;
    }
  }, [format, fps, speed, crop]);

  // Stop recording (manual)
  const stopRecording = useCallback(async () => {
    recordingRef.current = false;
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    onStopPlayback?.();
    processOutput();
  }, [onStopPlayback, processOutput]);

  if (!crop) return null;

  const handleStyle = (pos) => ({
    position: 'fixed',
    width: HANDLE_SIZE,
    height: HANDLE_SIZE,
    background: C.handle,
    borderRadius: 2,
    cursor: `${pos}-resize`,
    zIndex: 100002,
    ...(pos === 'nw' ? { left: crop.x - HANDLE_SIZE / 2, top: crop.y - HANDLE_SIZE / 2 } : {}),
    ...(pos === 'ne' ? { left: crop.x + crop.w - HANDLE_SIZE / 2, top: crop.y - HANDLE_SIZE / 2 } : {}),
    ...(pos === 'sw' ? { left: crop.x - HANDLE_SIZE / 2, top: crop.y + crop.h - HANDLE_SIZE / 2 } : {}),
    ...(pos === 'se' ? { left: crop.x + crop.w - HANDLE_SIZE / 2, top: crop.y + crop.h - HANDLE_SIZE / 2 } : {}),
  });

  return (
    <>
      {/* Dark overlay with crop cutout */}
      {phase === 'select' && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100000, pointerEvents: 'none',
        }}>
          {/* Top */}
          <div style={{ position: 'fixed', left: 0, top: 0, right: 0, height: crop.y, background: C.overlay, pointerEvents: 'auto' }} />
          {/* Bottom */}
          <div style={{ position: 'fixed', left: 0, top: crop.y + crop.h, right: 0, bottom: 0, background: C.overlay, pointerEvents: 'auto' }} />
          {/* Left */}
          <div style={{ position: 'fixed', left: 0, top: crop.y, width: crop.x, height: crop.h, background: C.overlay, pointerEvents: 'auto' }} />
          {/* Right */}
          <div style={{ position: 'fixed', left: crop.x + crop.w, top: crop.y, right: 0, height: crop.h, background: C.overlay, pointerEvents: 'auto' }} />
        </div>
      )}

      {/* Crop border */}
      {phase === 'select' && (
        <div
          style={{
            position: 'fixed',
            left: crop.x, top: crop.y, width: crop.w, height: crop.h,
            border: `2px dashed ${phase === 'recording' ? C.danger : C.border}`,
            zIndex: 100001,
            cursor: 'move',
            boxSizing: 'border-box',
          }}
          onMouseDown={(e) => handleMouseDown(e, 'move')}
        >
          {/* Dimension label */}
          <div style={{
            position: 'absolute', top: -24, left: '50%', transform: 'translateX(-50%)',
            background: C.bg, color: C.textDim, fontSize: 11, padding: '2px 8px',
            borderRadius: 4, whiteSpace: 'nowrap', fontFamily: "'IBM Plex Mono', monospace",
          }}>
            {crop.w * 2} x {crop.h * 2}px
          </div>
        </div>
      )}

      {/* Recording border (red pulse) */}
      {phase === 'recording' && (
        <div style={{
          position: 'fixed',
          left: crop.x, top: crop.y, width: crop.w, height: crop.h,
          border: `3px solid ${C.danger}`,
          zIndex: 100001,
          boxSizing: 'border-box',
          animation: 'recPulse 1s ease-in-out infinite',
          pointerEvents: 'none',
        }} />
      )}

      {/* Resize handles */}
      {phase === 'select' && ['nw', 'ne', 'sw', 'se'].map(pos => (
        <div
          key={pos}
          style={handleStyle(pos)}
          onMouseDown={(e) => handleMouseDown(e, pos)}
        />
      ))}

      {/* Control bar */}
      <div style={{
        position: 'fixed',
        left: crop.x, top: crop.y + crop.h + 12,
        zIndex: 100003,
        display: 'flex', alignItems: 'center', gap: 8,
        background: C.bg, border: `1px solid ${C.border}33`,
        borderRadius: 8, padding: '8px 14px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        fontFamily: "'IBM Plex Sans', sans-serif",
      }}>
        {phase === 'select' && (
          <>
            {/* Format toggle */}
            <div style={{ display: 'flex', gap: 2, background: 'rgba(255,255,255,0.06)', borderRadius: 4, padding: 2 }}>
              {['gif', 'webm'].map(f => (
                <button key={f} onClick={() => setFormat(f)} style={{
                  padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 3,
                  border: 'none', cursor: 'pointer',
                  background: format === f ? C.accent + '33' : 'transparent',
                  color: format === f ? C.accent : C.textDim,
                }}>{f.toUpperCase()}</button>
              ))}
            </div>

            {/* FPS */}
            <div style={{ display: 'flex', gap: 2, background: 'rgba(255,255,255,0.06)', borderRadius: 4, padding: 2 }}>
              {[30, 60].map(v => (
                <button key={v} onClick={() => setFps(v)} style={{
                  padding: '3px 8px', fontSize: 10, fontWeight: 600, borderRadius: 3,
                  border: 'none', cursor: 'pointer',
                  background: fps === v ? C.accent + '33' : 'transparent',
                  color: fps === v ? C.accent : C.textDim,
                }}>{v} FPS</button>
              ))}
            </div>

            {/* Speed */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 10, color: C.textDim }}>Speed</span>
              <div style={{ display: 'flex', gap: 2, background: 'rgba(255,255,255,0.06)', borderRadius: 4, padding: 2 }}>
                {[0.5, 1, 2].map(s => (
                  <button key={s} onClick={() => setSpeed(s)} style={{
                    padding: '3px 8px', fontSize: 10, fontWeight: 600, borderRadius: 3,
                    border: 'none', cursor: 'pointer',
                    background: speed === s ? C.accent + '33' : 'transparent',
                    color: speed === s ? C.accent : C.textDim,
                  }}>{s}x</button>
                ))}
              </div>
            </div>

            {/* Record button */}
            <button onClick={startRecording} style={{
              padding: '6px 16px', fontSize: 12, fontWeight: 700, borderRadius: 6,
              border: 'none', cursor: 'pointer',
              background: C.danger, color: '#fff',
            }}>
              Record
            </button>

            {/* Close */}
            <button onClick={onClose} style={{
              padding: '6px 10px', fontSize: 14, border: 'none', cursor: 'pointer',
              background: 'transparent', color: C.textDim, lineHeight: 1,
            }}>x</button>
          </>
        )}

        {phase === 'recording' && (
          <>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: C.danger, animation: 'recBlink 1s infinite' }} />
            <span style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>Recording... {progress} frames</span>
            <button onClick={stopRecording} style={{
              padding: '6px 16px', fontSize: 12, fontWeight: 700, borderRadius: 6,
              border: 'none', cursor: 'pointer',
              background: C.success, color: '#fff',
            }}>
              Stop
            </button>
          </>
        )}

        {phase === 'processing' && (
          <>
            <div style={{ width: 100, height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${progress}%`, height: '100%', background: C.accent, borderRadius: 3, transition: 'width 0.2s' }} />
            </div>
            <span style={{ fontSize: 12, color: C.accent, fontWeight: 600 }}>
              Processing... {progress}%
            </span>
          </>
        )}

        {phase === 'done' && (
          <>
            <span style={{ fontSize: 16 }}>✅</span>
            <span style={{ fontSize: 12, color: C.success, fontWeight: 600 }}>
              Downloaded!
            </span>
            <button onClick={() => { setPhase('select'); setProgress(0); }} style={{
              padding: '4px 12px', fontSize: 11, fontWeight: 600, borderRadius: 4,
              border: `1px solid ${C.border}`, background: 'transparent', color: C.text, cursor: 'pointer',
            }}>Record Again</button>
            <button onClick={onClose} style={{
              padding: '4px 12px', fontSize: 11, fontWeight: 600, borderRadius: 4,
              border: 'none', background: C.accent + '22', color: C.accent, cursor: 'pointer',
            }}>Done</button>
          </>
        )}
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes recPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes recBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.2; }
        }
      `}</style>
    </>
  );
};

export default ScreenRecorder;
